import { EventTimeline, EventType, IPowerLevelsContent, MatrixClient } from 'matrix-js-sdk/src';

import { CustomMeedioMatrixEvents, getUserPowerLevel } from 'utils/matrixUtils';
import { PermissionId, workspacesConfig } from 'utils/powerLevelsConfig';

type KeysMatching<T, V> = { [K in keyof T]-?: T[K] extends V ? K : never }[keyof T];
type Permission = {
  name: string;
  stateEvent?: EventType | string;
  requiredPowerLevel: number;
  action?: KeysMatching<Required<IPowerLevelsContent>, number>;
};

// V1 → V2 PermissionId mapping for this permissions gate (Wave 2 T-009a repair surface):
// - WorkspacesMembersInvite → WorkspaceInviteMembers (PL 30)
// - WorkspacesMembersRemove → WorkspaceRemoveMembers (PL 40)
// - WorkspacesGeneralEdit{Logo,Name,Url} → WorkspaceEditSettings (PL 70)
// - WorkspacesGeneralDelete → WorkspaceDelete (PL 98)
// - SubWorkspacesCreate → WorkspaceCreateSubWorkspace (PL 80)
// - WorkspacesMembersAdjustRole / WorkspacesRolesCreateRole / WorkspacesRolesAssignRoles /
//   WorkspacesRolesAdjustPermissions → WorkspaceManageRoles (PL 60)
// - RoomsCreate → WorkspaceCreateChat (PL 5) — chat-only UI gates
// - (V2 split) instant meetings → WorkspaceCreateInstantMeeting (PL 10)
// - RoomsCreateScheduled → WorkspaceCreateScheduledMeeting (PL 11)
// - RoomsCreatePrivate → WorkspaceCreatePrivateRoom (PL 12)
// - RoomsCreateWaitingList → WorkspaceCreatePrivateRoom (PL 12) — waiting-list rooms are private/knock
//   creation, not scheduled (PL 11).
const permissions = [
  {
    name: 'invitePeople',
    action: 'invite',
    requiredPowerLevel: workspacesConfig[PermissionId.WorkspaceInviteMembers].powerLevel,
  },
  {
    name: 'kickMember',
    action: 'kick',
    requiredPowerLevel: workspacesConfig[PermissionId.WorkspaceRemoveMembers].powerLevel,
  },
  {
    name: 'workspaceLogo',
    stateEvent: EventType.RoomAvatar,
    requiredPowerLevel: workspacesConfig[PermissionId.WorkspaceEditSettings].powerLevel,
  },
  {
    name: 'workspaceName',
    stateEvent: EventType.RoomName,
    requiredPowerLevel: workspacesConfig[PermissionId.WorkspaceEditSettings].powerLevel,
  },
  {
    name: 'workspaceUrl',
    stateEvent: EventType.RoomCanonicalAlias,
    requiredPowerLevel: workspacesConfig[PermissionId.WorkspaceEditSettings].powerLevel,
  },
  {
    name: 'workspaceDelete',
    stateEvent: CustomMeedioMatrixEvents.WORKSPACE_DELETE,
    requiredPowerLevel: workspacesConfig[PermissionId.WorkspaceDelete].powerLevel,
  },
  {
    name: 'createSubWorkspace',
    stateEvent: EventType.SpaceChild,
    requiredPowerLevel: workspacesConfig[PermissionId.WorkspaceCreateSubWorkspace].powerLevel,
  },
  {
    name: 'adjustRoles',
    stateEvent: CustomMeedioMatrixEvents.WORKSPACE_ROLES,
    requiredPowerLevel: workspacesConfig[PermissionId.WorkspaceManageRoles].powerLevel,
  },
  {
    name: 'createRoom',
    stateEvent: EventType.SpaceChild,
    requiredPowerLevel: workspacesConfig[PermissionId.WorkspaceCreateChat].powerLevel,
  },
  {
    name: 'createInstantMeeting',
    stateEvent: EventType.SpaceChild,
    requiredPowerLevel: workspacesConfig[PermissionId.WorkspaceCreateInstantMeeting].powerLevel,
  },
  {
    name: 'createScheduledMeeting',
    stateEvent: EventType.SpaceChild,
    requiredPowerLevel: workspacesConfig[PermissionId.WorkspaceCreateScheduledMeeting].powerLevel,
  },
  {
    name: 'createWaitingListMeeting',
    stateEvent: EventType.SpaceChild,
    requiredPowerLevel: workspacesConfig[PermissionId.WorkspaceCreatePrivateRoom].powerLevel,
  },
  {
    name: 'createPrivateMeeting',
    stateEvent: EventType.SpaceChild,
    requiredPowerLevel: workspacesConfig[PermissionId.WorkspaceCreatePrivateRoom].powerLevel,
  },
] as const satisfies readonly Permission[];

export type WorkspacePermissionName = (typeof permissions)[number]['name'];
export type WorkspacePermissionsState = Record<WorkspacePermissionName, boolean>;

const canISendWorkspaceStateEvent = (matrixClient: MatrixClient, roomId: string | null, eventType: string): boolean => {
  const room = matrixClient.getRoom(roomId ?? undefined);
  const userId = matrixClient.getUserId();
  if (!room || !userId) return false;

  const state = room.getLiveTimeline().getState(EventTimeline.FORWARDS);
  return state?.maySendStateEvent(eventType, userId) ?? false;
};

export const calculatePermissions = (matrixClient: MatrixClient | null, workspaceId: string | null) =>
  permissions.reduce(
    (acc, permission) => {
      const userId = matrixClient?.getUserId();
      const room = workspaceId ? matrixClient?.getRoom(workspaceId) : null;
      // Personal workspace (no workspaceId): no role model — keep all true.
      if (!workspaceId) {
        acc[permission.name] = true;
        return acc;
      }
      // Workspace set but prerequisites not ready: fail-closed until sync delivers the room.
      if (!matrixClient || !userId || !room) {
        acc[permission.name] = false;
        return acc;
      }

      const state = room.getLiveTimeline().getState(EventTimeline.FORWARDS);
      const canSendStateEvent =
        'stateEvent' in permission
          ? canISendWorkspaceStateEvent(matrixClient, workspaceId, permission.stateEvent)
          : true;
      const canPerformAction =
        'action' in permission && state
          ? state.hasSufficientPowerLevelFor(permission.action, getUserPowerLevel(room, userId))
          : true;
      const hasRequiredPowerLevel = getUserPowerLevel(room, userId) >= permission.requiredPowerLevel;
      acc[permission.name] = canSendStateEvent && hasRequiredPowerLevel && canPerformAction;

      return acc;
    },
    {} as Record<WorkspacePermissionName, boolean>
  );
